import { type MouseEvent, type ReactElement, type ReactNode } from 'react';
import { TABLE_PANEL_HEADER_HEIGHT } from '@/features/shared/components/tableSystem/tableConstants';
import { buildTableDividerPseudoSx } from '@/features/shared/components/tableSystem/tableDividerSx';
import {
  type buildToolbarIconButtonSx,
  buildToolbarIconCircleSx,
} from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import { getActionAriaLabel, getActionLabel } from '@/shared/copy/actions';
import { s, typographyTokens } from '@/theme/tokens';
import { Stack } from '@/ui/layout/Stack';
import { Box, Cancel, IconButton, Skeleton, Tooltip } from '@/ui/primitives';
import { SortOutlinedIcon } from '@/ui/primitives/icons';
import { ChartPanelTitle } from '../chartPanels/ChartPanelTitle';
import {
  HISTORY_TOOLBAR_SKELETON,
  HISTORY_TOOLBAR_TOOLTIP_SLOT_PROPS,
} from './EntityDailyHistoryPanel.constants';

type EntityDailyHistoryPanelHeaderProps = {
  title: string;
  dateRangeLabel: string | null;
  extraToolbarContent?: ReactNode;
  showSortControl?: boolean;
  showResetControl?: boolean;
  loading: boolean;
  canInteractWithToolbar: boolean;
  hasActiveSort?: boolean;
  hoverTarget?: 'sort' | 'reset' | null;
  toolbarControlSize?: number;
  toolbarIconButtonSx?: ReturnType<typeof buildToolbarIconButtonSx>;
  toolbarInactiveIconColor?: string;
  onSortButtonClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  onReset?: () => void;
  onHoverTargetChange?: (target: 'sort' | 'reset' | null) => void;
};

export const EntityDailyHistoryPanelHeader = ({
  title,
  dateRangeLabel: _dateRangeLabel,
  extraToolbarContent = null,
  showSortControl = true,
  showResetControl = true,
  loading,
  canInteractWithToolbar,
  hasActiveSort = false,
  hoverTarget = null,
  toolbarControlSize = 32,
  toolbarIconButtonSx,
  toolbarInactiveIconColor = 'text.secondary',
  onSortButtonClick,
  onReset,
  onHoverTargetChange,
}: EntityDailyHistoryPanelHeaderProps): ReactElement => {
  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        backgroundColor: 'background.paper',
        borderBottom: 'none',
        position: 'relative',
        '&::after': buildTableDividerPseudoSx('bottom'),
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap="sm"
        sx={{
          minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
          height: `${TABLE_PANEL_HEADER_HEIGHT}px`,
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            pl: s('xl'),
          }}
        >
          <ChartPanelTitle
            title={title}
            component="span"
            fontSizePx={17}
            fontWeight={typographyTokens.body.fontWeight}
            showInfoIcon={false}
          />
        </Box>
        <Box sx={{ ml: 'auto', pr: s('xl') }}>
          {loading ? (
            <Skeleton
              variant="rounded"
              width={HISTORY_TOOLBAR_SKELETON.width}
              height={HISTORY_TOOLBAR_SKELETON.height}
              sx={{
                transform: 'none',
                borderRadius: HISTORY_TOOLBAR_SKELETON.radius,
              }}
            />
          ) : (
            <Stack direction="row" alignItems="center" gap="sm">
              {extraToolbarContent}
              {showSortControl ? (
                <Tooltip
                  title={getActionLabel('sort')}
                  arrow
                  enterDelay={250}
                  slotProps={HISTORY_TOOLBAR_TOOLTIP_SLOT_PROPS}
                >
                  <Box component="span">
                    <IconButton
                      size="medium"
                      aria-label={getActionAriaLabel('open', 'sort')}
                      onClick={
                        canInteractWithToolbar && onSortButtonClick ? onSortButtonClick : undefined
                      }
                      disabled={!canInteractWithToolbar}
                      onMouseEnter={() => {
                        if (!canInteractWithToolbar) return;
                        onHoverTargetChange?.('sort');
                      }}
                      onMouseLeave={() => {
                        onHoverTargetChange?.(null);
                      }}
                      sx={[
                        toolbarIconButtonSx ?? {},
                        {
                          color:
                            canInteractWithToolbar && hasActiveSort
                              ? 'primary.main'
                              : toolbarInactiveIconColor,
                        },
                      ]}
                    >
                      <Box
                        sx={buildToolbarIconCircleSx(
                          canInteractWithToolbar && hoverTarget === 'sort',
                        )}
                      >
                        <SortOutlinedIcon
                          sx={{
                            color:
                              canInteractWithToolbar && hasActiveSort
                                ? 'primary.main'
                                : toolbarInactiveIconColor,
                            width: '22px !important',
                            height: '22px !important',
                          }}
                        />
                      </Box>
                    </IconButton>
                  </Box>
                </Tooltip>
              ) : null}
              {showResetControl ? (
                <Tooltip
                  title={getActionLabel('reset')}
                  arrow
                  enterDelay={250}
                  slotProps={HISTORY_TOOLBAR_TOOLTIP_SLOT_PROPS}
                >
                  <Box
                    component="span"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      height: `${String(toolbarControlSize)}px`,
                    }}
                  >
                    <IconButton
                      size="medium"
                      aria-label="Reset history sorting"
                      onClick={canInteractWithToolbar && onReset ? onReset : undefined}
                      disabled={!canInteractWithToolbar || !hasActiveSort}
                      onMouseEnter={() => {
                        if (!canInteractWithToolbar) return;
                        onHoverTargetChange?.('reset');
                      }}
                      onMouseLeave={() => {
                        onHoverTargetChange?.(null);
                      }}
                      sx={[
                        toolbarIconButtonSx ?? {},
                        canInteractWithToolbar && hasActiveSort ? { color: 'primary.main' } : {},
                      ]}
                    >
                      <Box
                        sx={buildToolbarIconCircleSx(
                          canInteractWithToolbar && hoverTarget === 'reset' && hasActiveSort,
                        )}
                      >
                        <Cancel sx={{ width: '22px !important', height: '22px !important' }} />
                      </Box>
                    </IconButton>
                  </Box>
                </Tooltip>
              ) : null}
            </Stack>
          )}
        </Box>
      </Stack>
    </Box>
  );
};
